import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { Member, MemberEffectivePermissionsDefault } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@nucleus-commerce/ui/components/sheet';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { useEffectivePermissions } from '../hooks/use-effective-permissions';

function AccessBlock({ access }: { access: MemberEffectivePermissionsDefault }) {
  const { t } = useTranslation('team');
  const everything = access.permissions.some((permission) => permission.key === '*');
  return (
    <div className="grid gap-2">
      <p className="text-sm">
        {t('effective.role')} <span className="font-medium">{access.role.name}</span>
      </p>
      {everything ? (
        <p className="text-muted-foreground text-sm">{t('effective.everything')}</p>
      ) : (
        <ul className="flex flex-wrap gap-1.5" aria-label={t('effective.permissionsLabel')}>
          {access.permissions.map((permission) => (
            <li key={permission.key}>
              <Badge
                variant={permission.source === 'granted' ? 'default' : 'secondary'}
                title={t(`effective.sources.${permission.source}`)}
                className="font-mono text-[11px]"
              >
                {permission.key}
                {permission.source === 'granted' ? ` · ${t('effective.sources.granted')}` : null}
              </Badge>
            </li>
          ))}
        </ul>
      )}
      {access.revokedPermissions.length > 0 ? (
        <p className="text-muted-foreground text-xs">
          {t('effective.removed')}{' '}
          <span className="font-mono">{access.revokedPermissions.join(', ')}</span>
        </p>
      ) : null}
    </div>
  );
}

export interface EffectivePermissionsSheetProps {
  /** The member to explain; null closes the sheet. */
  member: Member | null;
  onOpenChange: (open: boolean) => void;
}

/** What a member can do in each store and why: the role that applies, extras and removals. */
export function EffectivePermissionsSheet({
  member,
  onOpenChange,
}: EffectivePermissionsSheetProps) {
  const { t } = useTranslation('team');
  const message = useErrorMessage();
  const effective = useEffectivePermissions(member?.id ?? null);

  return (
    <Sheet open={member !== null} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>{t('effective.title')}</SheetTitle>
          <SheetDescription>
            {member ? `${member.user.name} · ${member.user.email}` : null}
          </SheetDescription>
        </SheetHeader>
        <div className="flex flex-col gap-6 px-4 pb-6">
          {effective.isPending ? (
            <Skeleton className="h-40 w-full" />
          ) : effective.isError ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{message(effective.error)}</AlertDescription>
            </Alert>
          ) : (
            <>
              <section className="grid gap-2">
                <h3 className="text-sm font-semibold">{t('effective.organization')}</h3>
                <p className="text-muted-foreground text-xs">{t('effective.organizationHint')}</p>
                <p className="font-mono text-xs">
                  {effective.data.organization.includes('*')
                    ? t('effective.everything')
                    : effective.data.organization.join(', ')}
                </p>
              </section>
              {effective.data.stores.map((store) => (
                <section key={store.storeId} className="grid gap-2 border-t pt-4">
                  <h3 className="flex flex-wrap items-center gap-2 text-sm font-semibold">
                    {store.storeName}
                    <Badge variant="outline">
                      {t(
                        store.source === 'store_role' ? 'effective.storeRole' : 'effective.default',
                      )}
                    </Badge>
                  </h3>
                  <AccessBlock access={store} />
                </section>
              ))}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
